import classNames from "classnames";
import styles from "./Select.module.scss";
import { Button, ClickAway, Span, Tag, Tooltip } from "../../index";
import _ from "lodash";
import { VariableSizeList } from "react-window";
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { AddIcon, CloseIcon, SearchIcon } from "../../../icons/monochrome";

const INCREMENTAL_HEIGHT = 40;
const SINGLE_ROW_HEIGHT = 48;
const DOUBLE_ROW_HEIGHT = 64;

const SingleLineOption = ({ style, className, onClick, option }) => {
    const { label, secondaryLabel, subLabel, tag, tooltip, value } = option;

    return <div style={style} className={classNames(styles["option"], className)} onClick={onClick}>
        {label || value} {!secondaryLabel && subLabel && <span className={styles['sub-label']}>{subLabel}</span>} {tag && <span className={styles.tag}><Tag level={12}>{tag}</Tag></span>}
        {tooltip && <Tooltip>{tooltip}</Tooltip>}
    </div>
}

const SingleLineCheckboxOption = ({ style, className, onClick, option }) => {
    const { label, secondaryLabel, subLabel, tag, tooltip, value } = option;
    return <div style={style} className={classNames(styles["option"], styles["checkbox-option"], className)} onClick={onClick}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="16" height="16" className={styles['bg']}/>
            <rect x="0.5" y="0.5" width="15" height="15" rx="1.5" className={styles['border']}/>
            <path
                d="M2.59996 7.57717C2.26787 7.9118 2.26787 8.45165 2.59996 8.78628L5.57564 11.7848C5.96677 12.1789 6.60411 12.1789 6.99524 11.7848L13.4005 5.3304C13.7327 4.99566 13.7336 4.45593 13.4026 4.12V4.12C13.0663 3.77872 12.5161 3.77774 12.1786 4.11782L6.28544 10.0562L3.81749 7.57631C3.48165 7.23885 2.93532 7.23924 2.59996 7.57717V7.57717Z"
                stroke="none" className={styles['check']}/>
        </svg>
        {label || value} {!secondaryLabel && subLabel && <span className={styles['sub-label']}>{subLabel}</span>} {tag && <span className={styles.tag}><Tag level={12}>{tag}</Tag></span>}
        {tooltip && <Tooltip>{tooltip}</Tooltip>}
    </div>
}

const MultiLineOption = ({ style, className, onClick, option }) => {
    const { label, secondaryLabel, tooltip } = option;

    return <div style={style} className={classNames(styles["option"], styles["multi-line"], className)} onClick={onClick}>
        <div className={styles["label"]}>{label}</div>
        <div className={styles["secondary-label"]}>{secondaryLabel}</div>
        {tooltip && <Tooltip>{tooltip}</Tooltip>}
    </div>
}

const MultiLineCheckboxOption = ({ style, className, onClick, option }) => {
    const { label, secondaryLabel, tooltip } = option;

    return <div style={style} className={classNames(styles["option"], styles["multi-line-checkbox"], className)} onClick={onClick}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="16" height="16" className={styles['bg']}/>
            <rect x="0.5" y="0.5" width="15" height="15" rx="1.5" className={styles['border']}/>
            <path
                d="M2.59996 7.57717C2.26787 7.9118 2.26787 8.45165 2.59996 8.78628L5.57564 11.7848C5.96677 12.1789 6.60411 12.1789 6.99524 11.7848L13.4005 5.3304C13.7327 4.99566 13.7336 4.45593 13.4026 4.12V4.12C13.0663 3.77872 12.5161 3.77774 12.1786 4.11782L6.28544 10.0562L3.81749 7.57631C3.48165 7.23885 2.93532 7.23924 2.59996 7.57717V7.57717Z"
                stroke="none" className={styles['check']}/>
        </svg>
        <div>
            <div className={styles["label"]}>{label}</div>
            <div className={styles["secondary-label"]}>{secondaryLabel}</div>
        </div>
        {tooltip && <Tooltip>{tooltip}</Tooltip>}
    </div>
}


const Option = ({ option, isSelected, toggleOption, setMenuOpen, style, isMulti }) => {
    const { secondaryLabel, isDisabled, value, noRemove } = option;
    const onClick = (e) => {
        if(!isDisabled && !noRemove) {
            toggleOption(option);
        }

        if (!isMulti) {
            setMenuOpen(false);
        }

        e.stopPropagation();
        e.preventDefault();
    };

    const className = { [styles["selected"]]: isSelected, [styles["disabled"]]: isDisabled || !value };

    if (!secondaryLabel && !isMulti) {
        return <SingleLineOption style={style} onClick={onClick} option={option} className={className}/>
    } else if (secondaryLabel && !isMulti) {
        return <MultiLineOption style={style} onClick={onClick} option={option} className={className}/>
    } else if (!secondaryLabel && isMulti) {
        return <SingleLineCheckboxOption style={style} onClick={onClick} option={option} className={className}/>
    } else if (secondaryLabel && isMulti) {
        return <MultiLineCheckboxOption style={style} onClick={onClick} option={option} className={className}/>
    } else {
        return <SingleLineOption style={style} onClick={onClick} option={option} className={className}/>
    }
}

const VirtualizedMenu = React.forwardRef(({ options, setMenuOpen, toggleOption, selectionState, isMulti }, variableSizeListRef) => {
    if (options.length < 6) return options.map(option => <Option setMenuOpen={setMenuOpen}
                                                                 toggleOption={toggleOption}
                                                                 isSelected={option.value ? selectionState?.valuesSet.has(option.value) : false}
                                                                 key={option.value?.id || option.value}
                                                                 option={option}
                                                                 isMulti={isMulti}
    />);

    const getItemSize = (index) => {
        if (options[index]?.secondaryLabel) {
            return DOUBLE_ROW_HEIGHT;
        } else {
            return SINGLE_ROW_HEIGHT;
        }
    };

    let selectedIndex = 0;
    let initialOffset = 0;

    if (selectionState) {
        selectedIndex = _.findIndex(options, { value: selectionState.selectedOptions[0].value }) || 0;
        const itemSize = getItemSize(selectedIndex); ////Assumes every row is same height

        if(selectedIndex < 6) { //first 6
            initialOffset = 0;
        } else if(options.length - selectedIndex < 6) { //last 6
            selectedIndex = options.length - 6;
            initialOffset = (selectedIndex * itemSize);
        } else { //middle
            initialOffset = ((selectedIndex - 3) * itemSize) + itemSize/2;
        }
    }

    return (
        <VariableSizeList
            itemSize={getItemSize}
            height={getItemSize(selectedIndex) * 6}
            itemCount={options.length}
            initialScrollOffset={initialOffset}
            className={styles["virtualized"]}
            ref={variableSizeListRef}
        >
            {({ index, style }) => {
                const option = options[index];
                return <Option
                    style={style}
                    setMenuOpen={setMenuOpen}
                    toggleOption={toggleOption}
                    isSelected={option.value ? selectionState?.valuesSet.has(option.value) : false}
                    key={option.value?.id || option.value}
                    option={option}
                    isMulti={isMulti}/>
            }}
        </VariableSizeList>
    )
})

function isVisible(element) {
    if (element) {
        // Check Visibility by using style property as pre-requisite, and if it is false then don't execute the isElementFullyVisible method.
        if(!isVisibleByStyles(element)) return false
        if(isElementNonVisible(element)) return false
    }
    return true
  }
  
function isVisibleByStyles(element) {
    const styles = window.getComputedStyle(element)
    return styles.visibility !== 'hidden' && styles.display !== 'none'
  }
/**
 * Returns whether element is visible or not by checking all the four corners of an
 * element visibilty.
 * @param {*} element 
 * @returns 
 */
function isElementNonVisible(element) {
    const boundingRect = element.getBoundingClientRect(); // Get the Boundaries of a passed element
    // Get the top, right, left and bottom pixels and add/substract one pixel to avoid the borders.
    const left = boundingRect.left + 1
    const right = boundingRect.right - 1
    const top = boundingRect.top + 1
    const bottom = boundingRect.bottom - 1
    
    // Use elementFromPoint method of document object to identify which element is visible at the location
    // and if it matches with the element passed then it is visible and returns false, else invisible and return true
    if(document.elementFromPoint(left, top) !== element) return true
    if(document.elementFromPoint(right, top) !== element) return true
    if(document.elementFromPoint(left, bottom) !== element) return true
    if(document.elementFromPoint(right, bottom) !== element) return true
    
    return false
  }


const PortedMenuOptions = ({ controlRef, menuPortalTarget, ...props }) => {
    const menuRef = useRef(null);

    useLayoutEffect(() => {
        const resize = () => {
            const controlRect = controlRef.current.getBoundingClientRect();
            const portalParentScrollTop = menuPortalTarget.parentNode.scrollTop;
            const portalParentScrollLeft = menuPortalTarget.parentNode.scrollLeft;

            menuRef.current.style.left = portalParentScrollLeft + controlRect.left + "px";
            menuRef.current.style.top = portalParentScrollTop + controlRect.top + controlRect.height + "px";
            menuRef.current.style.width = controlRect.width + "px";
        }

        resize();

        const resizeObserver = new ResizeObserver(() => {
            resize();
        });

        resizeObserver.observe(controlRef.current);

        menuRef.current.addEventListener("mouseenter", resize);

        return () => {
            resizeObserver.disconnect();
        }
    }, [controlRef, menuPortalTarget])

    return ReactDOM.createPortal(<MenuOptions {...props} menuRef={menuRef} className={styles["ported-menu"]}/>, menuPortalTarget)
}

const SearchBar = React.memo(({ setFilter, createOption, isCreatable }) => {
    const [value, setValue] = useState("");

    const throttledSetFilter = useMemo(() => {
        return _.debounce(value => {
            setFilter(value);
        }, 300);
    }, [setFilter]);

    const clear = () => {
        setTimeout(() => {
            setValue("");
            throttledSetFilter("");
        })
    };

    const handleChange = event => {
        const currValue = event.target.value;
        throttledSetFilter(currValue);
        setValue(currValue);
    };

    const handleKeyDown = event => {
        const currValue = event.target.value;
        if (event.key === 'Enter' || event.key === 'Backspace') {
            throttledSetFilter(currValue)
        }
        setValue(currValue);
    };

    return <div className={styles["search-bar"]}>
        <SearchIcon color="secondary" size={16}/>
        <input placeholder={isCreatable ? "Search or add" : "Search"}
               value={value}
               onChange={handleChange}
               onKeyDown={handleKeyDown}/>
        <div className={styles["search-controls"]}>
            {value && <button type="button" className={styles['close']} onClick={clear}><CloseIcon/></button>}
            {isCreatable && <Button variant="text" disabled={!value} onClick={() => {
                createOption(value);
                clear();
            }}><AddIcon size={16} style={{ marginRight: 0 }}/>Add</Button>}
        </div>
    </div>
});

const MenuOptions = ({ options, name, toggleOption, selectionState, acceptChanges, setMenuOpen, menuRef, isSearchable, isCreatable, isMulti, noOptionsMessage, componentId, isRequireAcceptOnChange, createOption, className }) => {
    const [filter, setFilter] = useState();
    const filteredOptions = useMemo(() => {
        if (filter) {
            const lowerCasedFilter = _.toLower(filter);
            return _.filter(options, option => {
                return (option.label && _.includes(_.toLower(option.label), lowerCasedFilter)) ||
                    (option.secondaryLabel && _.includes(_.toLower(option.secondaryLabel), lowerCasedFilter)) ||
                    _.toLower(option.value) === lowerCasedFilter;
            })
        } else {
            return options;
        }
    }, [filter, options]);

    const clickAway = useCallback((e) => {
        setMenuOpen(false);

        e.__multiSelectClose = componentId.current;
    }, [setMenuOpen, componentId])

    // If menuRef is undefined, then the control comes to MenuOptions directly else it comes from portedMenuOptions.
    // In case of menuRef is undefined, create a new ref and assign to div element.
    // useLayoutEffect statements must not execute for menuRef passed by portedMenuOptions.
    const isNotPorted = !menuRef;
    const virtualRef = useRef(null);
    if (isNotPorted) {
        menuRef = useRef(null);
    }
    let maxOptionsCount = 6; // Max number of options to show in the dropdown menu
    let isDownwardOptionsComplete = false; // Flag to inform that all the options for opening downward is complete
    let isUpwardOptionsComplete = false; // Flag to inform that all the options for opening upward is complete
    let isMenuVisible = false; // Flag to inform dropdown Menu option is visible

    // Function to return menu option row height
    const getItemSize = () => {
        if (options[0]?.secondaryLabel) {
            return DOUBLE_ROW_HEIGHT;
        } else {
            return SINGLE_ROW_HEIGHT;
        }
    };

    // Reset all the styles to default behavior and flags to intial state, if both downward and upward options are not available
    const resetStyles = () => {
        isDownwardOptionsComplete = false;
        isUpwardOptionsComplete = false;
        isMenuVisible = false;
        menuRef.current.style.overflowY = '';
        menuRef.current.style.maxHeight = '';
        menuRef.current.style.bottom = '';
        if (virtualRef.current && virtualRef.current._outerRef) {
            // If filteredOptions is more than 5, then set the max height for variableSizeList component and scroll
            // is already set for that in scss.
            virtualRef.current._outerRef.style.maxHeight = '';
        }
    }
    /**
     * Function to modify the styles to show dropdown menu in the view port based on the number of options it can fit in viewport
     * @param {*} isUpward - flag to decide which direction it is checking
     * @param {*} maxOptions - max number of menu options to be shown, by default it is 6
     */
    const modifyStyles = ( isUpward, maxOptions = 6 ) => {
        maxOptionsCount = maxOptions;
        while(!isMenuVisible && maxOptionsCount > 1) {
            // This is the max height that needs to be set for menu dropdown
            let menuMaxHeight = getItemSize() * maxOptionsCount;
            // If search is enabled, then add 40px input box height to the max height to make sure
            // search stays at the top without scroll.
            if (isSearchable) {
                menuMaxHeight += INCREMENTAL_HEIGHT;
            }

            // If accept/cancel button is enabled, then add 40px button height to the max height to make sure
            // button stays at the bottom fixed.
            if (isRequireAcceptOnChange) {
                menuMaxHeight += INCREMENTAL_HEIGHT;
            }
            // If filteredOptions is less than 6, then attach scroll view and max height as it is not set in scss file.
            if (filteredOptions.length < 6) {
                menuRef.current.style.overflowY = 'scroll';
                menuRef.current.style.maxHeight = menuMaxHeight + 'px';
            } else if (virtualRef.current && virtualRef.current._outerRef) {
                // If filteredOptions is more than 5, then set the max height for variableSizeList component and scroll
                // is already set for that in scss.
                virtualRef.current._outerRef.style.maxHeight = ((getItemSize() * maxOptionsCount) - 5) + 'px';
                menuRef.current.style.overflowY = '';
                menuRef.current.style.maxHeight = menuMaxHeight + 'px';
            } else {
                // reset the styles for No option scenario.
                menuRef.current.style.maxHeight = '';
                menuRef.current.style.overflowY = '';
            }
            // Check Menu is visible for maxOptionsCount, if not visible reduce the maxOptionsCount by 1 and test again
            isMenuVisible = isVisible(menuRef?.current);
            maxOptionsCount--;
            // If maxOptionsCount is set to minimum that is 2, and still Menu is not visible then update the flag that all options are complete
            if (maxOptionsCount == 1) {
               if (isUpward) {
                    isUpwardOptionsComplete = true;
               } else {
                    isDownwardOptionsComplete = true;
               }
            }      
        }
    }

    useLayoutEffect(
        () => {
            if (isNotPorted) {
                resetStyles();
                isMenuVisible = isVisible(menuRef?.current);
                // If to check downward options are available or not
                if (!isMenuVisible) {
                    modifyStyles(false);
                }
                // If to check upward options are available or not
                if (!isMenuVisible && isDownwardOptionsComplete ) {
                    const bottomPosition = (menuRef.current?.previousSibling?.getBoundingClientRect()?.height + 3 ) + 'px'
                    menuRef.current.style.bottom = bottomPosition;
                    modifyStyles(true);
                }
                // If to both downward and upward options are not availble then fall back to showing 2 options downwards
                if (!isMenuVisible && isDownwardOptionsComplete & isUpwardOptionsComplete) {
                    resetStyles();
                    modifyStyles(false, 2);
                }
            }
        },
        [menuRef, filteredOptions],
    );
    
    return <div ref={menuRef} className={classNames(styles["menu"], className)}>
        <ClickAway listener={clickAway} ignoreParentClick={true} capture closeOnModalClick={true}/>
        {isSearchable && <SearchBar setFilter={setFilter} createOption={createOption} isCreatable={isCreatable}/>}
        {filteredOptions?.length > 0 && <VirtualizedMenu options={filteredOptions} setMenuOpen={setMenuOpen} name={name} toggleOption={toggleOption} selectionState={selectionState} isMulti={isMulti} ref={virtualRef}/>}
        {!filteredOptions?.length && <div className={styles["no-options"]}>
            <Span bold>{noOptionsMessage ? noOptionsMessage() : "No Options"}</Span>
        </div>}
        {isRequireAcceptOnChange && <div className={styles["menu-actions"]}>
            <button onClick={() => {
                acceptChanges();
                setMenuOpen(false);
            }}>Accept
            </button>
            <button onClick={() => setMenuOpen(false)}>Cancel</button>
        </div>}
    </div>
};

const Menu = (props) => {
    if (props.menuPortalTarget) {
        return <PortedMenuOptions {...props}/>
    } else {
        return <MenuOptions {...props}/>
    }
}

export default Menu;